import {
  Box,
  Checkbox,
  HStack,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ReactElement, useMemo } from "react";
import { GroupBase, OptionProps, components } from "chakra-react-select";

import { Select } from "components/Select";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import textStyles from "theme/textStyles";
import { inputSize, mobileStyleBreakpoint } from "util/styleHelpers";
import useGetValidCsvEmailRecipients from "hooks/api/documents/useGetValidCsvEmailRecipients";
import { ExportReportParamsObjectTypeEnum } from "api";
import { FilterStateValue } from "contexts/RequestParamsProvider";

const SELECT_MAX_WIDTH = mobileStyleBreakpoint("unset", "480px");
const SELECT_MENU_LIST_HEIGHT = mobileStyleBreakpoint("208px", "340px");

const validRecipientToSelectOption = (
  recipient: ValidCsvEmailRecipent
): SelectOption => ({
  value: recipient.email as string,
  label: recipient.name as string,
});

const Option = (props: OptionProps<unknown, boolean, GroupBase<unknown>>) => {
  const { data } = props as OptionProps<
    SelectOption,
    boolean,
    GroupBase<SelectOption>
  >;
  return (
    <components.Option {...props}>
      <HStack alignItems="center">
        <Checkbox pointerEvents="none" isChecked={props.isSelected} readOnly />
        <VStack align="left" spacing={0}>
          <Box
            as="label"
            fontWeight="semibold"
            textStyle="body1"
            paddingLeft={2}
          >
            {data.label}
          </Box>
          <Box as="label" textStyle="body1" paddingLeft={2}>
            {data.value}
          </Box>
        </VStack>
      </HStack>
    </components.Option>
  );
};

const ValidCsvEmailRecipentSelect = ({
  objectType,
  searchString,
  filters,
  selectedUsers,
  setSelectedUsers,
}: {
  objectType: ExportReportParamsObjectTypeEnum;
  searchString: string;
  filters: Record<string, FilterStateValue>;
  selectedUsers: SelectOption[];
  setSelectedUsers: (selectedUsers: SelectOption[]) => void;
}): ReactElement => {
  const selectedCustomerCodes = useSelectedCustomerCode();

  const size = useBreakpointValue(inputSize, { ssr: false });

  const { data: validRecipients, isLoading } = useGetValidCsvEmailRecipients(
    selectedCustomerCodes,
    {
      cardCodes: selectedCustomerCodes,
      objectType,
      searchString: searchString || undefined,
      ...filters,
    }
  );

  const options = useMemo(
    () => validRecipients?.map(validRecipientToSelectOption),
    [validRecipients]
  );

  return (
    <Select
      backspaceRemovesValue={false}
      controlShouldRenderValue={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      components={{
        Option,
      }}
      size={size as any}
      value={selectedUsers}
      onChange={(newValue) => setSelectedUsers(newValue as SelectOption[])}
      isClearable={false}
      isSearchable={true}
      options={options}
      isDisabled={(validRecipients?.length ?? 0) <= 1}
      isLoading={isLoading}
      isMulti={true}
      placeholder={
        <Box color="grey.500" width="368px" paddingLeft={2}>
          {selectedUsers?.length === 0
            ? `Select users`
            : `${selectedUsers?.length} user(s) selected`}
        </Box>
      }
      styles={{
        option: (provided, state) => ({
          ...provided,
          borderBottom: "1px solid #E0E1E3",
          padding: "8px",
          color: state.isDisabled ? "#A3A4A6" : "unset",
          backgroundColor: "unset",
          ":hover": {
            backgroundColor: "#DAF8FF",
          },
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
      }}
      selectChakraStyleOverrides={{
        input: (provided) => ({
          ...provided,
          width: "400px",
          paddingLeft: 2,
        }),
        control: (provided) => ({
          ...provided,
          maxH: "84px",
          overflowY: "auto",
        }),
        container: (provided) => ({
          ...provided,
          backgroundColor: "white",
          maxW: SELECT_MAX_WIDTH,
        }),
        multiValue: (provided) => ({
          ...provided,
          ...textStyles.label2,
          backgroundColor: "white",
        }),
        menuList: (provided) => ({
          ...provided,
          bgColor: "grey.50",
          maxHeight: SELECT_MENU_LIST_HEIGHT,
        }),
      }}
    />
  );
};

export default ValidCsvEmailRecipentSelect;
